/**
 * Penelope's settings editor, shared by the full panel (/penelope), the Penelope
 * tab of the trade ticket and the /live strip: the lean selector and the knobs
 * of one TIER (basic = the seven BASIC_KNOB_KEYS, advanced = every knob in KNOB_GROUPS with the
 * variant picker and clipboard). Controlled: the caller supplies the config and
 * a `save` that PATCHes (or edits a draft before the game exists).
 */
import { useState } from 'react';
import { cn } from '@/lib/infra/utils';
import { isVariantSpec, variantName, type PenelopeConfig, type PenelopeLean, type PenelopeVariant, type PenelopeVariantSpec } from '@/hooks/usePenelope';
import { VARIANT_GROUPS } from './knobs';
import { BASIC_KNOB_KEYS, KNOB_GROUPS, type Knob } from './knobs';

/** Knob keys an overlay may carry: everything the editor lists (never enabled / mode / variants / lean). */
const OVERLAY_KEYS: ReadonlySet<string> = new Set(KNOB_GROUPS.flatMap((g) => g.knobs.map((k) => k.key)));
export const MAX_VARIANTS = 8;

const UNKNOWN = '?';

/** A box's legal range per numeric knob (GET /api/penelope/profile `bounds`): the inputs carry the real ceilings. */
export type PenelopeKnobBounds = Record<string, { min: number; max: number }>;

export function KnobRow({ k, cfg, editable, save, bound, fieldError }: {
  k: Knob; cfg: PenelopeConfig; editable: boolean; save: (patch: Partial<PenelopeConfig>) => void;
  /** This box's range for the knob, over the knob's own typo bounds. */
  bound?: { min: number; max: number };
  /** The relay's refusal of this field on the last write. */
  fieldError?: string;
}) {
  const v = cfg[k.key];
  const id = `penelope-knob-${k.key}`;
  const [problem, setProblem] = useState<string | null>(null);
  const min = k.kind === 'number' ? (bound?.min ?? k.min) : undefined;
  const max = k.kind === 'number' ? (bound?.max ?? k.max) : undefined;
  // A knob another setting switches off (max behind under "at the best bid"): greyed, with the reason, never hidden —
  // the saved value is kept for when the other setting changes back.
  const unused = k.disabledWhen?.(cfg as unknown as Record<string, unknown>) ?? null;
  const canEdit = editable && unused === null;
  let input;
  if (k.kind === 'bool') {
    input = <input id={id} type="checkbox" disabled={!canEdit} checked={v === true} onChange={(e) => save({ [k.key]: e.target.checked })} />;
  } else if (k.kind === 'select') {
    input = (
      <select id={id} disabled={!canEdit} value={typeof v === 'string' ? v : ''} onChange={(e) => save({ [k.key]: e.target.value })} className="border-border bg-background rounded border px-1 py-0.5 font-mono">
        {typeof v !== 'string' && <option value="">{UNKNOWN}</option>}
        {k.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  } else {
    input = (
      <span className="flex items-center gap-1 font-mono">
        <input
          id={id}
          type="number"
          disabled={!canEdit}
          step={k.step}
          min={min}
          max={max}
          defaultValue={typeof v === 'number' ? v : ''}
          key={`${k.key}:${String(v)}`}
          placeholder={typeof v === 'number' ? undefined : UNKNOWN}
          aria-invalid={problem !== null || fieldError !== undefined ? true : undefined}
          onBlur={(e) => {
            const next = Number(e.target.value);
            if (e.target.value === '' || next === v) { setProblem(null); return; }
            // Out of range is said, never silently dropped (Update 01: a value the box would not run is refused visibly).
            if (!Number.isFinite(next)) { setProblem('not a number'); return; }
            if ((min !== undefined && next < min) || (max !== undefined && next > max)) {
              setProblem(`${min ?? '…'}–${max ?? '…'} on this box`);
              return;
            }
            setProblem(null);
            save({ [k.key]: next });
          }}
          className={cn('border-border bg-background w-20 rounded border px-1 py-0.5 text-right', (problem || fieldError) && 'border-red-500')}
        />
        <span className="text-muted-foreground w-5">{k.unit ?? ''}</span>
      </span>
    );
  }
  return (
    <div className={cn('flex items-start justify-between gap-2 py-0.5', unused !== null && 'opacity-60')} data-unused={unused !== null ? 'true' : undefined}>
      <label htmlFor={id} className="flex flex-col">
        <span>{k.label}</span>
        <span className="text-muted-foreground text-[10px]">{k.hint}</span>
        {unused !== null && <span className="text-[10px] text-amber-600">{unused}</span>}
        {(problem || fieldError) && <span role="alert" className="text-[10px] text-red-500">{problem ?? fieldError}</span>}
      </label>
      {input}
    </div>
  );
}

/** The variants that run beside a game: the built-in one-factor ones by group, and the researcher's own overlays
 *  (name + the knobs it changes, as JSON). `save` receives the whole new list. */
export function VariantPicker({ variants, editable, busy = false, save }: {
  variants: PenelopeVariant[];
  editable: boolean;
  busy?: boolean;
  save: (variants: PenelopeVariant[]) => void;
}) {
  const [name, setName] = useState('');
  const [overlay, setOverlay] = useState('');
  const [legs, setLegs] = useState<PenelopeVariantSpec['legs']>('noNo');
  const [problem, setProblem] = useState<string | null>(null);
  const names = new Set(variants.map(variantName));
  const custom = variants.filter(isVariantSpec);
  const full = variants.length >= MAX_VARIANTS;
  const add = () => {
    const n = name.trim();
    if (!/^[a-z0-9._-]{1,32}$/.test(n)) return setProblem('name: 1–32 of a-z 0-9 . _ -');
    if (names.has(n) || n === 'baseline') return setProblem('that name is taken');
    let parsed: unknown;
    try {
      parsed = overlay.trim() === '' ? {} : JSON.parse(overlay);
    } catch {
      return setProblem('overlay must be JSON, e.g. {"minEdgeCents": 1, "maxBehindCents": 3}');
    }
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return setProblem('overlay must be a JSON object');
    const keys = Object.keys(parsed as Record<string, unknown>);
    const unknown = keys.filter((k) => !OVERLAY_KEYS.has(k));
    if (unknown.length > 0) return setProblem(`not a knob: ${unknown.join(', ')}`);
    if (keys.length === 0 && legs === 'noNo') return setProblem('an overlay has to change something (a knob, or the legs)');
    setProblem(null);
    save([...variants, { name: n, overlay: parsed as Record<string, unknown>, ...(legs && legs !== 'noNo' ? { legs } : {}) }]);
    setName('');
    setOverlay('');
    setLegs('noNo');
  };
  const chip = 'rounded border px-1 py-px font-mono text-[10px]';
  return (
    <div className="flex flex-col gap-1">
      {VARIANT_GROUPS.map((grp) => (
        <div key={grp.label} className="mb-1 flex flex-wrap items-center gap-1">
          <span className="text-muted-foreground w-36 text-[10px]">{grp.label}</span>
          {grp.names.map((n) => {
            const on = names.has(n);
            return (
              <button
                key={n}
                type="button"
                disabled={!editable || busy || (!on && full)}
                aria-pressed={on}
                onClick={() => save(on ? variants.filter((v) => variantName(v) !== n) : [...variants, n])}
                className={cn(chip, on ? 'border-sky-500 text-sky-500' : 'border-border/60 text-muted-foreground hover:text-foreground')}
              >
                {n}
              </button>
            );
          })}
        </div>
      ))}
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-muted-foreground w-36 text-[10px]" title="Your own overlays: the game's settings with these knobs changed, run beside it in simulation">your own</span>
        {custom.length === 0 && <span className="text-muted-foreground text-[10px]">none yet</span>}
        {custom.map((v) => (
          <span key={v.name} className={cn(chip, 'border-violet-500 text-violet-500 flex items-center gap-1')} title={`${JSON.stringify(v.overlay)}${v.legs && v.legs !== 'noNo' ? ` · legs ${v.legs}` : ''}`}>
            {v.name}
            {editable && (
              <button type="button" disabled={busy} aria-label={`remove variant ${v.name}`} onClick={() => save(variants.filter((x) => variantName(x) !== v.name))} className="hover:text-foreground">
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      {editable && (
        <div className="flex flex-wrap items-end gap-1" aria-label="Add a custom variant">
          <label className="flex flex-col text-[10px]">
            <span className="text-muted-foreground">name</span>
            <input value={name} disabled={busy || full} onChange={(e) => setName(e.target.value)} placeholder="edge1.behind3" className="border-border bg-background w-28 rounded border px-1 py-0.5 font-mono" />
          </label>
          <label className="flex flex-1 flex-col text-[10px]">
            <span className="text-muted-foreground">overlay (JSON of knobs to change)</span>
            <input value={overlay} disabled={busy || full} onChange={(e) => setOverlay(e.target.value)} placeholder='{"minEdgeCents": 1, "maxBehindCents": 3}' className="border-border bg-background min-w-48 rounded border px-1 py-0.5 font-mono" />
          </label>
          <label className="flex flex-col text-[10px]">
            <span className="text-muted-foreground">legs</span>
            <select value={legs ?? 'noNo'} disabled={busy || full} onChange={(e) => setLegs(e.target.value as PenelopeVariantSpec['legs'])} className="border-border bg-background rounded border px-1 py-0.5 font-mono">
              <option value="noNo">NO + NO</option>
              <option value="yesYes">YES + YES</option>
              <option value="oneMarket">one market</option>
            </select>
          </label>
          <button type="button" disabled={busy || full} onClick={add} className="border-border rounded border px-2 py-0.5 text-[10px] font-bold uppercase">
            add variant
          </button>
          {full && <span className="text-muted-foreground text-[10px]">max {MAX_VARIANTS} on a game</span>}
          {problem && <span role="alert" className="text-[10px] text-red-500">{problem}</span>}
        </div>
      )}
    </div>
  );
}

/** Copy the settings as JSON, or paste JSON over them (a preset of your own). Only knobs are pasted — never
 *  `enabled`, `mode` or `variants`. */
export function ConfigClipboard({ cfg, editable, busy = false, save }: { cfg: PenelopeConfig; editable: boolean; busy?: boolean; save: (patch: Partial<PenelopeConfig>) => void }) {
  const [note, setNote] = useState<string | null>(null);
  const knobsOnly = (c: Record<string, unknown>): Partial<PenelopeConfig> => Object.fromEntries(Object.entries(c).filter(([k]) => OVERLAY_KEYS.has(k) || k === 'lean'));
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(knobsOnly(cfg), null, 2));
      setNote('copied');
    } catch {
      setNote('could not reach the clipboard');
    }
  };
  const paste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const parsed = JSON.parse(text) as Record<string, unknown>;
      if (typeof parsed !== 'object' || parsed === null) throw new Error('not an object');
      const patch = knobsOnly(parsed);
      if (Object.keys(patch).length === 0) throw new Error('no knobs in it');
      save(patch);
      setNote(`pasted ${Object.keys(patch).length} setting(s)`);
    } catch (e) {
      setNote(`paste failed: ${(e as Error).message}`);
    }
  };
  return (
    <div className="flex items-center gap-2 text-[10px]">
      <button type="button" onClick={() => void copy()} className="border-border/60 text-muted-foreground hover:text-foreground rounded border px-1.5 py-px font-mono" title="Copy every knob as JSON — keep it as your own preset">
        copy JSON
      </button>
      {editable && (
        <button type="button" disabled={busy} onClick={() => void paste()} className="border-border/60 text-muted-foreground hover:text-foreground rounded border px-1.5 py-px font-mono" title="Paste JSON knobs over this game's settings (never arms it or changes its mode)">
          paste JSON
        </button>
      )}
      {note && <span className="text-muted-foreground">{note}</span>}
    </div>
  );
}

/** Penelope's two tiers (2026-10-08). `basic` is what every trader gets: the seven knobs in
 *  BASIC_KNOB_KEYS, flat, with "Max Long" driving the lean cap too (one cap to reason about), no variants, no
 *  clipboard. `advanced` is every knob in collapsible groups (the first two open) plus variants and the clipboard.
 *  `advanced-only` is `advanced` minus what `basic` already shows — for a disclosure rendered UNDER a basic block,
 *  so no knob (and its input id) appears twice on one screen. */
export type PenelopeTier = 'basic' | 'advanced' | 'advanced-only';

export function PenelopeSettings({ cfg, editable, busy = false, save, teamA, teamB, tier = 'advanced', bounds, fieldErrors }: {
  cfg: PenelopeConfig;
  editable: boolean;
  busy?: boolean;
  save: (patch: Partial<PenelopeConfig>) => void;
  teamA: string;
  teamB: string;
  tier?: PenelopeTier;
  /** This box's ranges (GET /api/penelope/profile), so an input past the ceiling says so before the relay refuses it. */
  bounds?: PenelopeKnobBounds;
  /** The relay's per-field refusals of the last write. */
  fieldErrors?: Array<{ field: string; error: string }>;
}) {
  const errorOf = (key: string): string | undefined => fieldErrors?.find((e) => e.field === key)?.error;
  const basic = tier === 'basic';
  const leans: Array<{ value: PenelopeLean; label: string }> = [{ value: 'a', label: teamA }, { value: 'neutral', label: 'no lean' }, { value: 'b', label: teamB }];
  const wanted = (k: Knob): boolean => (tier === 'basic' ? BASIC_KNOB_KEYS.has(k.key) : tier === 'advanced-only' ? !BASIC_KNOB_KEYS.has(k.key) : true);
  const groups = KNOB_GROUPS.map((g) => ({ ...g, knobs: g.knobs.filter(wanted) })).filter((g) => g.knobs.length > 0);
  // Basic has one "Max Long": the lean cap follows it, so a lean never has a second cap the trader did not set.
  const saveTiered = (patch: Partial<PenelopeConfig>) =>
    save(basic && typeof patch.maxNetContracts === 'number' ? { ...patch, leanMaxNetContracts: patch.maxNetContracts } : patch);
  return (
    <div className="flex flex-col gap-2 text-xs">
      {/* PEN-01: the basic tier has the side switches instead (usePenelopeSides); lean is an advanced knob now —
          which side's net cap is the lean cap, and which side needs lean-skew less edge. */}
      {tier !== 'basic' && (
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground" title="Lean: that side's net cap is the lean cap, and it needs lean-skew less edge to add (the other side more). Which sides she may buy at all is the side switches.">lean</span>
        {leans.map((o) => (
          <button
            key={o.value}
            type="button"
            disabled={!editable || busy}
            aria-pressed={cfg.lean === o.value}
            onClick={() => cfg.lean !== o.value && save({ lean: o.value })}
            className={cn('rounded border px-1.5 py-0.5 font-mono text-[10px] uppercase', cfg.lean === o.value ? 'border-foreground/60 text-foreground' : 'border-border/60 text-muted-foreground hover:text-foreground')}
          >
            {o.label}
          </button>
        ))}
      </div>
      )}
      {basic
        ? groups.flatMap((grp) => grp.knobs).map((k) => <KnobRow key={k.key} k={k} cfg={cfg} editable={editable} save={saveTiered} bound={bounds?.[k.key]} fieldError={errorOf(k.key)} />)
        : groups.map((grp, i) => (
          <details key={grp.title} open={i < 2} className="border-border/60 border-t pt-1">
            <summary className="text-muted-foreground cursor-pointer text-[10px] uppercase tracking-wide">{grp.title}</summary>
            {grp.knobs.map((k) => <KnobRow key={k.key} k={k} cfg={cfg} editable={editable} save={save} bound={bounds?.[k.key]} fieldError={errorOf(k.key)} />)}
          </details>
        ))}
      {!basic && (
        <details className="border-border/60 border-t pt-1">
          <summary className="text-muted-foreground cursor-pointer text-[10px] uppercase tracking-wide" title="Each variant re-runs this game with something changed, on the same data. Variants are always simulated.">
            Run beside it, simulated ({(cfg.variants ?? []).length} on, max {MAX_VARIANTS})
          </summary>
          <div className="mt-1">
            <VariantPicker variants={cfg.variants ?? []} editable={editable} busy={busy} save={(variants) => save({ variants })} />
          </div>
        </details>
      )}
      {!basic && <ConfigClipboard cfg={cfg} editable={editable} busy={busy} save={save} />}
    </div>
  );
}
